import React from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';

import usePageTitle from '../../hooks/UsePageTitle';
const StartNewSchool = () => {
  usePageTitle('Starting a New School');

  return (
    <div className="page-container">
      <div className="grid">
        <div className="col-12">
          <h1 className="text-center">School Management Services – Starting a New School</h1>
          <Divider />
        </div>

        {/* Introduction Section */}
        <div className="col-12 md:col-6">
          <img 
            src='/images/school (1).jpg' 
            alt="Starting a New School" 
            className="w-full border-round shadow-2 mb-4" 
            style={{ maxHeight: '400px', objectFit: 'cover' }}
          />
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <p className="text-lg line-height-3">
              Starting a school is not just a business venture; it's a mission to shape minds and build the future of society. 
              However, launching a new educational institution comes with numerous challenges – regulatory, financial, 
              infrastructural, academic, and operational.
            </p>
            <p className="text-lg line-height-3 mt-3">
              At SnP Finvisors, we provide end-to-end advisory and implementation support for entrepreneurs and educational 
              trusts who wish to set up schools that are academically excellent, financially viable, and operationally efficient.
            </p>
          </Card>
        </div>

        {/* Understanding the Vision Section */}
        <div className="col-12 mt-5">
          <h2>Understanding the Vision</h2>
          <Divider />
          <div className="flex flex-column md:flex-row gap-5">
            <div className="flex-1">
              <p className="text-xl line-height-3">
                Every school begins with a vision—be it to offer international-standard education, focus on holistic 
                development, cater to underserved communities, or promote alternative learning models. We begin by 
                understanding your vision, goals, target audience, and capacity, and then translate that vision into 
                a detailed execution plan.
              </p>
            </div>
            <img 
              src='/images/school (2).jpg' 
              alt="School Vision" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '300px', objectFit: 'cover' }}
            />
          </div>
        </div>

        {/* Services Section */}
        <div className="col-12 mt-5">
          <h2>Our Comprehensive "Start a School" Services</h2>
          <Divider />
          
          <div className="grid">
            {/* Service 1 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="1. Feasibility Study & Business Planning" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Detailed feasibility analysis including demographics, competition, local demand, and policy framework</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Financial projections, ROI analysis, break-even timelines, and capital planning</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identification of suitable school models: CBSE, ICSE, IB, IGCSE, or State Board</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 2 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="2. Regulatory Approvals & Legal Structuring" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Guidance on trust/society/Section 8 company registration as required by educational boards</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>End-to-end support in obtaining NOCs from education departments, land clearance, and affiliation-related approvals</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Advisory on land use compliance and municipal regulations</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 3 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="3. School Infrastructure Planning" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Assistance in campus design planning based on board norms, including classrooms, laboratories, sports facilities, libraries</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Coordination with architects and vendors to align design with pedagogical needs and compliance requirements</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Infrastructure budgeting and monitoring to avoid cost overruns</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 4 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="4. Academic Framework & Curriculum Design" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Selection of curriculum and academic structure in accordance with board requirements</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Assistance in framing academic policies, assessment strategies, and co-scholastic development programs</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Incorporation of technology, experiential learning, and digital tools in academic delivery</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 5 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="5. Affiliation Process Management" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Full support in preparing documentation for CBSE, ICSE, IB, or IGCSE affiliation</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Ensuring compliance with all infrastructure, faculty, and governance requirements</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Liaisons with board officials, audit preparation, and follow-up actions</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 6 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="6. Financial Management & Budgeting" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Development of operating budgets and CAPEX plans</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Advisory on fee structuring, financial controls, working capital, and fundraising</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Implementation of school accounting systems and fee collection modules</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 7 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="7. Staff Recruitment & HR Policy Framework" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Designing job descriptions, staffing models, and HR policies for academic and non-academic staff</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Support in recruiting principals, teaching staff, admin team, and support staff</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Structuring of payroll, performance appraisal systems, and training plans</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 8 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="8. Technology & ERP Implementation" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Selection and implementation of School ERP systems for admissions, attendance, academics, communication</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Integration of LMS (Learning Management Systems), smart classroom tools, and digital content platforms</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 9 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="9. Marketing & Admissions Planning" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Brand strategy and launch campaigns including logo, tagline, and prospectus development</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Website design, digital marketing, and parent engagement strategies</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Support in setting up admission teams, processes, and documentation</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 10 */}
            <div className="col-12">
              <Card title="10. Project Management & Monitoring" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Dedicated project manager to oversee the execution of timelines, vendor coordination, and regulatory compliance</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Regular project reviews, reporting to trustees/promoters, and milestone tracking</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>

        {/* Why Choose Us Section */}
        <div className="col-12 mt-5">
          <h2>Why Choose SnP Finvisors?</h2>
          <Divider />
          <div className="grid">
            <div className="col-12 md:col-6 lg:col-3">
              <Card className="h-full">
                <h3 className="mt-0">Specialized Experience</h3>
                <p>
                  We bring deep domain expertise in educational institution consulting, having worked with K-12 schools, 
                  international schools, and educational trusts.
                </p>
              </Card>
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <Card className="h-full">
                <h3 className="mt-0">One-Stop Solution</h3>
                <p>
                  From concept to classroom, we manage every element of starting a school under one roof.
                </p>
              </Card>
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <Card className="h-full">
                <h3 className="mt-0">Custom Strategy</h3>
                <p>
                  Our plans are not templated — we customize each school model based on your goals, location, and audience.
                </p>
              </Card>
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <Card className="h-full">
                <h3 className="mt-0">Regulatory Know-How</h3>
                <p>
                  Our team is well-versed with central and state board policies, helping fast-track approvals and affiliations.
                </p>
              </Card>
            </div>
            <div className="col-12">
              <Card>
                <h3 className="mt-0">Sustainable Setup</h3>
                <p>
                  We ensure financial planning that promotes long-term sustainability and reputation management for the school.
                </p>
              </Card>
            </div>
          </div>
        </div>

        {/* Who Can Benefit Section */}
        <div className="col-12 mt-5">
          <h2>Who Can Benefit?</h2>
          <Divider />
          <div className="grid">
            <div className="col-12 md:col-6 lg:col-3">
              <div className="flex align-items-center mb-3">
                <i className="pi pi-user text-primary mr-3 text-xl"></i>
                <span className="font-semibold">Entrepreneurs entering the education sector</span>
              </div>
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <div className="flex align-items-center mb-3">
                <i className="pi pi-users text-primary mr-3 text-xl"></i>
                <span className="font-semibold">NGOs and Trusts planning schools for community upliftment</span>
              </div>
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <div className="flex align-items-center mb-3">
                <i className="pi pi-building text-primary mr-3 text-xl"></i>
                <span className="font-semibold">Existing school owners launching new branches</span>
              </div>
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <div className="flex align-items-center mb-3">
                <i className="pi pi-briefcase text-primary mr-3 text-xl"></i>
                <span className="font-semibold">Corporate CSR arms setting up educational initiatives</span>
              </div>
            </div>
          </div>
        </div>

        {/* Closing Section */}
        <div className="col-12 mt-5">
          <div className="flex flex-column md:flex-row gap-5">
            <div className="flex-1">
              <h2>Let's Build a School Together</h2>
              <Divider />
              <p className="text-xl line-height-3">
                Creating a school is creating a legacy. At SnP Finvisors, we ensure that your vision is translated 
                into a fully operational, academically sound, and financially sustainable institution. From the first 
                blueprint to the first bell – we walk with you every step of the way.
              </p>
              <p className="text-xl line-height-3 mt-3 font-semibold">
                Talk to us today and take your first step toward building an institution that shapes the future.
              </p>
            </div>
            <img 
              src='/images/school (4).jpg' 
              alt="Building a School Legacy" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '350px', objectFit: 'cover' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartNewSchool;